import { supabase } from "@/lib/supabase";

export interface ParticipantRow {
  id: string;
  event_id: string;
  name: string;
  email: string;
  role: string | null;
  company: string | null;
  contact_for_matches: string | null;
  goals: string | null;
  looking_for: string | null;
  can_help_with: string | null;
  video_storage_path: string | null;
  transcription: string | null;
  profile_json: Record<string, unknown> | null;
  pitch_status: "pending" | "submitted" | "approved" | "flagged" | "rejected";
  ai_flags: string[] | null;
  /** V1 PAEI grid output — deprecated, kept for legacy DB rows. */
  pitch_paei: PitchPaei | null;
  pitch_paei_generated_at: string | null;
  /** V2 three-block read of the pitch — the canonical output. */
  pitch_analysis: PitchAnalysis | null;
  pitch_analysis_generated_at: string | null;
  created_at: string;
}

export type PaeiLetter = "P" | "A" | "E" | "I";
export type PaeiLabel = "Producer" | "Administrator" | "Entrepreneur" | "Integrator";

/**
 * Legacy PAEI grid read produced by the first version of
 * process-meetup-pitch. Kept on the type only so historical DB rows
 * still parse — new code should read PitchAnalysis instead.
 */
export interface PitchPaei {
  primary: PaeiLetter;
  primary_label: PaeiLabel;
  secondary: PaeiLetter | null;
  secondary_label: PaeiLabel | null;
  scores: { P: number; A: number; E: number; I: number };
  summary: string;
  evidence: { letter: PaeiLetter; quote: string; note: string }[];
}

/**
 * Three-block qualitative read of the recorded pitch — spec frame P4.
 * Produced by process-meetup-pitch on the same call that builds
 * profile_json. Surfaced on PitchAnalysisPage as the reward for
 * recording; never visible to other participants.
 */
export interface PitchAnalysis {
  /** 2-3 sentences. Who the speaker reads as from the delivery. */
  portrait: string;
  /** 1-2 sentences. How the pitch lands against the stated goal. */
  positioning: string;
  /** 2 concrete tips for the next take. */
  strengthen: string[];
}

export interface PreviousParticipant {
  participant_id: string;
  event_id: string;
  event_title: string;
  event_date: string;
  video_storage_path: string | null;
  profile_json: Record<string, unknown> | null;
  looking_for: string | null;
  can_help_with: string | null;
  contact_for_matches: string | null;
  role: string | null;
  company: string | null;
  name: string;
}

export async function findPreviousParticipant(
  eventId: string,
  email: string,
): Promise<PreviousParticipant | null> {
  const { data, error } = await supabase.rpc("find_previous_meetup_participant", {
    p_current_event_id: eventId,
    p_email: email.toLowerCase().trim(),
  });
  if (error) throw new Error(error.message);
  const rows = (data ?? []) as PreviousParticipant[];
  return rows[0] ?? null;
}

export async function updateParticipantProfile(
  participantId: string,
  patch: { looking_for?: string; can_help_with?: string; contact_for_matches?: string },
): Promise<ParticipantRow> {
  const { data, error } = await supabase.rpc("update_meetup_participant_profile", {
    p_participant_id: participantId,
    p_looking_for: patch.looking_for ?? null,
    p_can_help_with: patch.can_help_with ?? null,
    p_contact_for_matches: patch.contact_for_matches ?? null,
  });
  if (error) throw new Error(error.message);
  return data as ParticipantRow;
}

export async function deleteParticipantPitch(participantId: string): Promise<void> {
  const { error } = await supabase.rpc("delete_meetup_participant_pitch", {
    p_participant_id: participantId,
  });
  if (error) throw new Error(error.message);
}

export async function submitMeetupSurvey(args: {
  participantId: string;
  relevance: number;
  metSomeone: boolean;
  nps: number;
  comment?: string;
}): Promise<string> {
  const { data, error } = await supabase.rpc("submit_meetup_survey", {
    p_participant_id: args.participantId,
    p_relevance: args.relevance,
    p_met_someone: args.metSomeone,
    p_nps: args.nps,
    p_comment: args.comment ?? null,
  });
  if (error) throw new Error(error.message);
  return data as string;
}

export async function addParticipantManually(
  eventId: string,
  input: { name: string; email: string; role?: string; company?: string },
): Promise<ParticipantRow> {
  const { data, error } = await supabase
    .from("event_participants")
    .insert({
      event_id: eventId,
      name: input.name,
      email: input.email.toLowerCase(),
      role: input.role || null,
      company: input.company || null,
    })
    .select("*")
    .single();
  if (error) throw new Error(error.message);
  return data as ParticipantRow;
}

export interface RegisterInput {
  event_id: string;
  name: string;
  email: string;
  contact_for_matches: string;
  role?: string;
  company?: string;
}

/**
 * Organizer-side fetch — relies on `participants_select_organizer` RLS
 * policy. Returns nothing for anon callers (use fetchEventFeed instead).
 */
export async function fetchParticipants(eventId: string): Promise<ParticipantRow[]> {
  const { data, error } = await supabase
    .from("event_participants")
    .select("*")
    .eq("event_id", eventId)
    .order("created_at", { ascending: false });
  if (error) throw new Error(error.message);
  return (data ?? []) as ParticipantRow[];
}

export interface EventFeedRow {
  id: string;
  event_id: string;
  name: string;
  role: string | null;
  company: string | null;
  video_storage_path: string | null;
  pitch_status: ParticipantRow["pitch_status"];
  profile_json: Record<string, unknown> | null;
  looking_for: string | null;
  can_help_with: string | null;
  email: string | null;            // populated only for is_self
  contact_for_matches: string | null; // populated only for is_self
  is_self: boolean;
  is_profile_complete: boolean;
  has_video: boolean;
  score: number | null;
  match_reason: string | null;
  created_at: string;
}

/**
 * Anon-participant feed — security-definer RPC gated by participantId.
 * Returns participants of the same event with email/contact masked except
 * for the caller's own row. Includes both video pitches and text-only
 * profiles (is_profile_complete && no video); rejected/flagged peers are
 * hidden. Without p_limit the function returns the full sorted list —
 * preserves the previous contract for callers that need everyone (e.g.
 * MatchesPage "similar goals" recommendations).
 */
export async function fetchEventFeed(participantId: string): Promise<EventFeedRow[]> {
  const { data, error } = await supabase.rpc("find_event_participants", {
    p_participant_id: participantId,
  });
  if (error) throw new Error(error.message);
  return (data ?? []) as EventFeedRow[];
}

export interface FeedPageCursor {
  /** coalesce(score, -1) of the last row in the previous page. */
  afterScore: number;
  afterId: string;
}

export interface FeedPage {
  rows: EventFeedRow[];
  /** Cursor to pass into the next call, or null when no more pages. */
  nextCursor: FeedPageCursor | null;
}

/**
 * Paginated feed — returns up to `limit` non-self rows after the optional
 * cursor, plus the caller's own row pinned to position 0 on every page so
 * the FeedPage can read its own state without an extra roundtrip.
 *
 * The RPC asks for `limit + 1` rows internally; we strip the extra row
 * and use its (score, id) as the next-page cursor. When fewer than
 * `limit + 1` rows come back there is no next page.
 */
export async function fetchEventFeedPage(
  participantId: string,
  limit: number,
  cursor: FeedPageCursor | null,
): Promise<FeedPage> {
  const { data, error } = await supabase.rpc("find_event_participants", {
    p_participant_id: participantId,
    p_limit: limit,
    p_after_score: cursor?.afterScore ?? null,
    p_after_id: cursor?.afterId ?? null,
  });
  if (error) throw new Error(error.message);
  const all = (data ?? []) as EventFeedRow[];

  // Self pin: extract self before slicing so it never counts toward the
  // page limit and never becomes the next cursor.
  const selfRow = all.find((r) => r.is_self) ?? null;
  const others = all.filter((r) => !r.is_self);

  const overflow = others.length > limit;
  const rows = overflow ? others.slice(0, limit) : others;
  const last = rows[rows.length - 1];
  const nextCursor: FeedPageCursor | null =
    overflow && last ? { afterScore: last.score ?? -1, afterId: last.id } : null;

  // Self stays at the top of the first page only — subsequent pages don't
  // need it because the FeedPage already has it from the first response.
  const isFirstPage = cursor === null;
  const finalRows = isFirstPage && selfRow ? [selfRow, ...rows] : rows;
  return { rows: finalRows, nextCursor };
}

export interface ParticipantSignInResult {
  participantId: string;
  eventId: string;
  name: string;
  email: string;
}

export async function signInParticipantByEmail(
  slug: string,
  email: string,
): Promise<ParticipantSignInResult | null> {
  const { data, error } = await supabase.rpc("find_meetup_participant_by_email", {
    p_slug: slug,
    p_email: email,
  });
  if (error) throw new Error(error.message);
  const rows = (data ?? []) as Array<{
    participant_id: string;
    event_id: string;
    name: string;
    email: string;
  }>;
  const row = rows[0];
  if (!row) return null;
  return {
    participantId: row.participant_id,
    eventId: row.event_id,
    name: row.name,
    email: row.email,
  };
}

/**
 * Lightweight check whether a participant has linked their Telegram chat
 * to receive notifications. Used by the "Connect Telegram" UI to show
 * either a Connect button or a "Connected ✓" badge. RPC is anon-grantable
 * by design — the participant id is the only entry point here, so leaking
 * "is X connected?" is acceptable.
 */
export async function getParticipantTelegramStatus(
  participantId: string,
): Promise<{ isLinked: boolean; username: string | null } | null> {
  const { data, error } = await supabase.rpc("get_meetup_participant_telegram_status", {
    p_participant_id: participantId,
  });
  if (error) throw new Error(error.message);
  const row = (data as Array<{ is_linked: boolean; username: string | null }> | null)?.[0];
  if (!row) return null;
  return { isLinked: row.is_linked, username: row.username };
}

export async function fetchParticipantById(id: string): Promise<ParticipantRow | null> {
  // Anon participants are blocked by RLS from reading event_participants
  // directly; the security-definer RPC returns their own row by id.
  const { data, error } = await supabase.rpc("find_meetup_participant_self", {
    p_participant_id: id,
  });
  if (error) throw new Error(error.message);
  return (data as ParticipantRow | null) ?? null;
}

export async function registerParticipant(input: RegisterInput): Promise<ParticipantRow> {
  // After the security lockdown anon cannot SELECT event_participants, so
  // .insert().select() fails on RETURNING. Use the security-definer RPC.
  const { data, error } = await supabase.rpc("register_meetup_participant", {
    p_event_id: input.event_id,
    p_name: input.name,
    p_email: input.email,
    p_contact_for_matches: input.contact_for_matches,
    p_role: input.role ?? null,
    p_company: input.company ?? null,
  });
  if (error) throw new Error(error.message);
  return data as ParticipantRow;
}

export async function updateGoalFields(
  participantId: string,
  looking_for: string,
  can_help_with: string,
): Promise<void> {
  const { error } = await supabase.rpc("set_meetup_participant_goals", {
    p_participant_id: participantId,
    p_looking_for: looking_for,
    p_can_help_with: can_help_with,
  });
  if (error) throw new Error(error.message);
}

export async function updateVideoPath(participantId: string, path: string): Promise<void> {
  const { error } = await supabase.rpc("set_meetup_participant_video", {
    p_participant_id: participantId,
    p_video_path: path,
  });
  if (error) throw new Error(error.message);
}

export async function transcribeMeetupAudio(
  participantId: string,
  audio: Blob,
): Promise<string> {
  const form = new FormData();
  form.append("file", audio, "speech.webm");
  form.append("participant_id", participantId);
  const { data, error } = await supabase.functions.invoke<{
    ok: boolean;
    text?: string;
    error?: string;
  }>("transcribe-meetup-audio", { body: form });
  if (error) throw new Error(error.message);
  if (!data?.ok || !data.text) {
    throw new Error(data?.error ?? "Transcription failed. Try again.");
  }
  return data.text;
}

export async function updatePitchStatus(
  participantId: string,
  status: ParticipantRow["pitch_status"],
): Promise<void> {
  const { error } = await supabase
    .from("event_participants")
    .update({ pitch_status: status })
    .eq("id", participantId);
  if (error) throw new Error(error.message);
}

/**
 * Polish the participant's two goal fields via GPT — sharpens wording for
 * the Feed without inventing facts. Returns a new pair of strings the UI
 * shows back to the user for review/edit before they hit Continue.
 *
 * The Edge Function validates the participant_id against
 * event_participants but doesn't persist anything — `set_meetup_participant_goals`
 * (called on Continue) is the single writer.
 */
export async function structureMeetupGoals(
  participantId: string,
  lookingFor: string,
  canHelpWith: string,
): Promise<{ lookingFor: string; canHelpWith: string }> {
  const { data, error } = await supabase.functions.invoke<{
    ok: boolean;
    looking_for?: string;
    can_help_with?: string;
    error?: string;
  }>("structure-meetup-goals", {
    body: {
      participant_id: participantId,
      looking_for: lookingFor,
      can_help_with: canHelpWith,
    },
  });
  if (error) throw new Error(error.message);
  if (!data?.ok) {
    throw new Error(data?.error ?? "Could not structure the answers. Try again.");
  }
  return {
    lookingFor: typeof data.looking_for === "string" ? data.looking_for : lookingFor,
    canHelpWith: typeof data.can_help_with === "string" ? data.can_help_with : canHelpWith,
  };
}

// ============================================================================
// Authenticated meetup signup flow
// ============================================================================

export async function signupLinkMeetupParticipant(
  eventId: string,
): Promise<ParticipantRow> {
  const { data, error } = await supabase.rpc("signup_link_meetup_participant", {
    p_event_id: eventId,
  });
  if (error) throw new Error(error.message);
  return data as ParticipantRow;
}

export interface CompleteProfileInput {
  eventId: string;
  contactForMatches: string;
  role?: string;
  company?: string;
}

export async function completeMeetupParticipantProfile(
  input: CompleteProfileInput,
): Promise<ParticipantRow> {
  const { data, error } = await supabase.rpc("complete_meetup_participant_profile", {
    p_event_id: input.eventId,
    p_contact_for_matches: input.contactForMatches,
    p_role: input.role ?? null,
    p_company: input.company ?? null,
  });
  if (error) throw new Error(error.message);
  return data as ParticipantRow;
}

export async function claimLegacyMeetupParticipations(): Promise<number> {
  const { data, error } = await supabase.rpc("claim_legacy_meetup_participations");
  if (error) throw new Error(error.message);
  return (data as number) ?? 0;
}

export interface MyMeetupEvent {
  participant_id: string;
  event_id: string;
  slug: string;
  title: string;
  event_date: string;
  location: string | null;
  event_status: "draft" | "active" | "completed" | "cancelled";
  pitch_status: ParticipantRow["pitch_status"];
  role_at_event: string | null;
  company_at_event: string | null;
  has_video: boolean;
  registered_at: string;
  participant_count: number;
  match_reveal_hours: number;
  my_match_count: number;
}

export async function listMyMeetupEvents(): Promise<MyMeetupEvent[]> {
  const { data, error } = await supabase.rpc("list_my_meetup_events");
  if (error) throw new Error(error.message);
  return (data ?? []) as MyMeetupEvent[];
}

export async function fetchMyParticipantForEvent(
  eventId: string,
): Promise<ParticipantRow | null> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;
  const { data, error } = await supabase
    .from("event_participants")
    .select("*")
    .eq("event_id", eventId)
    .eq("user_id", user.id)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return (data as ParticipantRow | null) ?? null;
}
